"use client"

import { IconButton } from "@/elements"
import React, { FC, useState } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { BreadCrumbs } from "@/components"
import { FlagIcon } from "@/icons/outline"
import { ReportProjectDialog } from "@/components/ReportDialog"
import type { NovaSession } from "@/types"
import { LikeProject } from "./LikeProject"
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline"
import { useRouter } from "next-nprogress-bar"
import { DeleteProjectModal } from "./DeleteProjectModal"
import { decodeGraphqlNodeId } from "@/tracking/decodeGqlNodeToId"

const ProjectDesktopHeaderFragment = graphql(`
  fragment ProjectDesktopHeader on CreativeWorkPortfolioProject {
    __typename
    id
    title
    viewerHasLiked
    creative {
      id
      ... on IndividualCreative {
        handle
        displayName
        user {
          id
          name
        }
      }
    }
  }
`)

interface Props {
  project: FragmentType<typeof ProjectDesktopHeaderFragment> | undefined | null
  serverSession: NovaSession | null
  isViewersProject: boolean
}

export const ProjectDesktopHeader: FC<Props> = ({ isViewersProject, serverSession, ...props }) => {
  const project = useFragment(ProjectDesktopHeaderFragment, props.project)
  const router = useRouter()
  const creative = project?.creative?.__typename === "IndividualCreative" ? project.creative : null
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const isAuthorized = !!serverSession?.accessToken
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const onEdit = () => {
    const handle = serverSession?.user?.creative?.handle
    router.push(`/${handle}/projects/form?projectId=${project?.id}&entry=project`)
  }

  const onDelete = () => {
    setDeleteModalOpen(true)
  }

  if (!isAuthorized) {
    return null
  }

  return (
    <>
      <ReportProjectDialog projectId={project?.id!} open={reportModalOpen} onOpenChange={setReportModalOpen} />
      <div className="flex w-full flex-row items-center justify-between py-5">
        <BreadCrumbs
          breadCrumbs={[
            { text: creative?.displayName || creative?.user?.name || "", link: `/${creative?.handle}` },
            { text: project?.title || "", link: undefined },
          ]}
        />
        {!isViewersProject && (
          <div className="flex flex-row items-center gap-4">
            <LikeProject
              projectId={decodeGraphqlNodeId(project?.id).numericalId || ""}
              entryPoint="portfolio-project-header"
            />
            <IconButton Icon={FlagIcon} onClick={() => setReportModalOpen(true)} variant="white" />
          </div>
        )}
        {isViewersProject && (
          <>
            <DeleteProjectModal
              isOpen={deleteModalOpen}
              onOpenChange={setDeleteModalOpen}
              handle={serverSession.user?.creative?.handle || ""}
              projectId={project?.id!}
            />
            <div className="flex flex-row items-center gap-4">
              <IconButton
                variant="white"
                className="hidden sm:flex"
                Icon={PencilIcon}
                onClick={onEdit}
                data-prevent-nprogress={true}
              />
              <IconButton variant="white" className="hidden sm:flex" Icon={TrashIcon} onClick={onDelete} />
            </div>
          </>
        )}
      </div>
    </>
  )
}
